import { Input, Spinner } from '@crm/ui';
import { ChevronRight, Image as ImageIcon, Search } from 'lucide-react';
import { useEffect, useState } from 'react';

export interface SearchListItem {
  id: string;
  title: string;
  subtitle?: string;
  /** Informacao curta a direita (preco, duracao). */
  meta?: string;
  /** Miniatura a esquerda - a foto do servico ajuda a escolher. */
  image?: string | null;
  active?: boolean;
}

interface SearchListProps {
  placeholder: string;
  /** Acao que fica na mesma linha do campo (ex.: cadastrar cliente). */
  action?: React.ReactNode;
  items: SearchListItem[];
  onSelect: (id: string) => void;
  onSearch: (term: string) => void;
  isLoading?: boolean;
  emptyLabel: string;
}

/**
 * Tela de busca do wizard: um campo grande e a lista logo abaixo, com a seta
 * indicando que a linha e selecionavel. Desenhada para o dedo, nao para o
 * mouse - a linha inteira e o alvo.
 */
export function SearchList({
  placeholder,
  action,
  items,
  onSelect,
  onSearch,
  isLoading = false,
  emptyLabel,
}: SearchListProps) {
  const [term, setTerm] = useState('');

  // Uma busca por pausa na digitacao, nao por tecla.
  useEffect(() => {
    const timer = setTimeout(() => onSearch(term.trim()), 300);

    return () => clearTimeout(timer);
  }, [term, onSearch]);

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <div className="relative min-w-0 flex-1">
          <Search className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground" />
          <Input
            autoFocus
            className="h-14 pl-12 text-base"
            placeholder={placeholder}
            value={term}
            onChange={(event) => setTerm(event.target.value)}
          />
        </div>

        {action}
      </div>

      {isLoading ? (
        <div className="flex justify-center py-10">
          <Spinner className="h-6 w-6 text-muted-foreground" />
        </div>
      ) : items.length === 0 ? (
        <p className="rounded-lg border border-dashed border-border bg-muted/40 px-4 py-10 text-center text-base text-muted-foreground">
          {emptyLabel}
        </p>
      ) : (
        // No celular a lista encosta nas bordas da modal: moldura e margem
        // encolhem o alvo do dedo sem informar nada. A borda volta no desktop,
        // onde a lista nao ocupa a largura toda.
        <ul className="-mx-4 divide-y divide-border border-y border-border bg-card sm:mx-0 sm:overflow-hidden sm:rounded-lg sm:border">
          {items.map((item) => (
            <li key={item.id}>
              <button
                type="button"
                onClick={() => onSelect(item.id)}
                className={`flex w-full items-center gap-3 px-4 py-4 text-left transition-colors hover:bg-muted/60 ${
                  item.active ? 'bg-primary/10' : ''
                }`}
              >
                {/* Lista com fotos mantem a coluna da miniatura mesmo sem
                    imagem: sem o espaco reservado, um item sem foto sobe para a
                    margem e a lista fica com dois alinhamentos. */}
                {item.image !== undefined ? (
                  item.image ? (
                    <img
                      src={item.image}
                      alt=""
                      loading="lazy"
                      className="h-12 w-12 shrink-0 rounded-md border border-border object-cover"
                    />
                  ) : (
                    <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-md border border-border bg-muted text-muted-foreground">
                      <ImageIcon className="h-5 w-5" aria-hidden />
                    </span>
                  )
                ) : null}

                <span className="min-w-0 flex-1">
                  <span className="block truncate text-base font-medium">{item.title}</span>
                  {item.subtitle ? (
                    <span className="block truncate text-sm text-muted-foreground">
                      {item.subtitle}
                    </span>
                  ) : null}
                  {/* No celular preco e duracao vao abaixo: o nome do servico
                      nao pode ser truncado para caber com eles na mesma linha. */}
                  {item.meta ? (
                    <span className="block text-sm font-medium text-muted-foreground sm:hidden">
                      {item.meta}
                    </span>
                  ) : null}
                </span>

                {item.meta ? (
                  <span className="hidden whitespace-nowrap text-sm font-medium text-muted-foreground sm:block">
                    {item.meta}
                  </span>
                ) : null}

                <ChevronRight className="h-5 w-5 shrink-0 text-muted-foreground" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
